import { useEffect, useState } from 'react';
import { CalendarCheck, CalendarPlus, RotateCw } from 'lucide-react';
import { GoogleCalendarShootResponse, type GoogleCalendarShootState } from '@diana/contracts';
import { useAdminApi } from '../../app/AdminApiContext.js';
import { Button } from '../../ui/index.js';
import './calendar.css';

export function CalendarSyncControl({
  state,
  eventUrl,
  onSync,
  busy = false,
  error = false,
}: {
  state: GoogleCalendarShootState['state'];
  eventUrl?: string | null;
  onSync(): void;
  busy?: boolean;
  error?: boolean;
}) {
  return (
    <div className="shoot-calendar-control">
      {state === 'Synced' && eventUrl ? (
        <a
          className="dui-button dui-button--quiet dui-button--small"
          href={eventUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="Open in Google Calendar"
        >
          <CalendarCheck size={18} aria-hidden="true" />
          Calendar
        </a>
      ) : (
        <Button
          variant="quiet"
          size="small"
          leadingIcon={state === 'Error' ? RotateCw : CalendarPlus}
          loading={busy}
          disabled={state === 'Pending' || state === 'MissingTime'}
          onClick={onSync}
        >
          {state === 'Error' ? 'Retry sync' : state === 'Pending' ? 'Syncing…' : 'Sync calendar'}
        </Button>
      )}
      {state === 'MissingTime' ? (
        <small>Set start and duration</small>
      ) : error || state === 'Error' ? (
        <small role="alert">Could not sync</small>
      ) : null}
    </div>
  );
}
export function ShootCalendarControl({
  id,
  revision,
  fallback,
}: {
  id: string;
  revision: number;
  fallback: React.ReactNode;
}) {
  const api = useAdminApi(),
    [value, setValue] = useState<GoogleCalendarShootState | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false);
  useEffect(() => {
    let active = true,
      failures = 0,
      timer: ReturnType<typeof setTimeout> | undefined;
    async function load() {
      try {
        const result = await api.get(
          `/api/v1/admin/sessions/${id}/calendar`,
          GoogleCalendarShootResponse,
        );
        if (active) {
          failures = 0;
          setError(false);
          setValue(result);
          if (result.state === 'Pending') timer = setTimeout(() => void load(), 5000);
        }
      } catch {
        if (active) {
          setError(true);
          setValue((previous) =>
            previous?.state === 'Pending' ? { ...previous, state: 'Error' } : previous,
          );
          if (++failures < 3) timer = setTimeout(() => void load(), 10000);
        }
      }
    }
    void load();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [api, id, revision, busy]);
  async function sync() {
    setBusy(true);
    setError(false);
    try {
      setValue(
        await api.request(`/api/v1/admin/sessions/${id}/calendar/retry`, {
          method: 'POST',
          schema: GoogleCalendarShootResponse,
        }),
      );
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  if (!value || value.state === 'NotConnected' || value.state === 'NotScheduled')
    return <>{fallback}</>;
  return <CalendarSyncControl {...value} onSync={() => void sync()} busy={busy} error={error} />;
}
